import React, {useEffect, useState} from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { Paper, Tooltip, withStyles} from "@material-ui/core";
import {useDispatch, useSelector} from "react-redux";
import {bbIsRequestedAttackDetailed, bbShortenString} from "../../../util/bbHelpers";
import {fetchRequestedAttackDetails} from "../../../actions";
import base64 from 'react-native-base64';
import BBCodeCopyContainer from "../controllers/BBCodeCopyContainer";
import Skeleton from "react-loading-skeleton";
import BBIconPlay from "../../../assets/bbCustomIcons/BBIconPlay";
import {HashLink as Link} from "react-router-hash-link";

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    wrapper: {
        // margin: theme.spacing(1),
        position: 'relative',
    },
});

const BBDialog = (props) =>  {
    const [open, setOpen] = React.useState(false);
    const {
        // classes,
        attackId,
        customerId,
        isDisabled = false,
        displayIcon = <BBIconPlay/>,
        displayState = null, //1 for play, 3 for stop,
        singlePulse = false,
    } = props;
    const {requestedAttacks, loadingRequestedAttackDetails, loadingRequestedAttackDetailsFail} = useSelector(({insider}) => insider);
    const [localLoading, setLocalLoading] = useState(false);
    const attackObject = requestedAttacks ?  requestedAttacks[customerId] ? requestedAttacks[customerId][attackId] ? requestedAttacks[customerId][attackId] : null : null : null;
    const base64Object = attackObject ? bbIsRequestedAttackDetailed(attackObject) ? attackObject["Attack Command Base64"] : null : null;
    // const assessments = customerAssessments ? customerAssessments[customerId] ? customerAssessments[customerId] : null : null;

    const promptCommand = base64Object ? base64.decode(base64Object) : null;
    const todayDateString = new Date().toISOString();

    const dispatch = useDispatch();

    useEffect(() => {
        // console.log("printing complianceTogglesLoading, ", complianceTogglesLoading);
        //check for this customer and this target's loader.
        // const isLoading = complianceTogglesLoading[customerId] ? complianceTogglesLoading[customerId][controlId] ? complianceTogglesLoading[customerId][controlId] : null : null;
        // const isError = complianceTogglesFail[customerId] ? complianceTogglesFail[customerId][controlId] ? complianceTogglesFail[customerId][controlId] : null : null;

        if (!loadingRequestedAttackDetails || loadingRequestedAttackDetailsFail) {
            setLocalLoading(false);
        }
    }, [dispatch, requestedAttacks, loadingRequestedAttackDetails, loadingRequestedAttackDetailsFail]);

    const handleClickOpen = () => {
        //fetch the attack if there is no base64 Object
        if(!bbIsRequestedAttackDetailed(attackObject)) {

            dispatch(fetchRequestedAttackDetails({
                attackId: attackId,
                customerId: customerId
            }))

        }
        // setCodeCopied(false);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const DialogTitle = withStyles(styles)((props) => {
        const { children, classes, onClose, ...other } = props;
        return (
            <MuiDialogTitle disableTypography className={classes.root} {...other}>
                <Typography variant="h6">{children}</Typography>
                {onClose ? (
                    <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                ) : null}
            </MuiDialogTitle>
        );
    });

    const buttonColor = () => {
        switch(displayState) {
            case 1:
                return "bg-electric-blue text-white btn-floating pulse"
            case 3:
                return "bg-bb-risk-highest text-white"
            default:
                return null
        }
    }

    return (
        <>
            <Tooltip title={isDisabled ? "Action unavailable" : "View and download attack files."}>
                <span>
                    <Button
                        variant="outlined"
                        color="default"
                        className={isDisabled ? "" : `${buttonColor()} ${singlePulse ? "btn-floating pulse pulse-single" : ""}`}
                        onClick={handleClickOpen}
                        disabled={isDisabled}
                        // size={"small"}
                    >
                    {displayIcon}
                </Button>
                </span>
            </Tooltip>
            <Dialog
                fullWidth={true}
                maxWidth={"lg"}
                open={open}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
            >
                <DialogTitle id="customized-dialog-title" onClose={handleClose}>
                    Launch Insider Attack

                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <h3>You are in control.</h3>
                        <p>Copy and execute this code or download and run the executable script on the host you would like to attack in order
                            to launch this Attack on Demand. No communications or attack actions will occur until you do this. <em>You will be able to access this
                        code and download applicable script files until you successfully execute the script.</em></p>
                    </DialogContentText>
                    {loadingRequestedAttackDetails || !bbIsRequestedAttackDetailed(attackObject)
                    ? <Paper className={"p-2"} elevation={5}><Skeleton count={4}/></Paper>
                    : base64Object && promptCommand
                            ? <BBCodeCopyContainer
                                displayText={promptCommand}
                                showDownload
                                fileName={`[SAFE-(RedTeam)]_BB_Insider_ID-${bbShortenString(attackId, 8)}-downloaded_on_${bbShortenString(todayDateString, 19)}.bat`}
                            />
                            : <em>No code found.  This attack is either not yet ready to launch or has been de-provisioned.</em>}
                    <hr/>
                    <DialogContentText>
                        <h4>What happens next?</h4>
                        <p>If the script is successfully started (which will require that the host you choose can communicate with the Internet),
                            the status of this attack will be reflected in the Insider Attacks Status Log, where you can track its status.  If the attack is successfully attempted, you will then be able to
                            inspect the details of the attack.</p>
                        <p>This code will only work once.  You will need to request another Attack on Demand to test a different host or to test the same host at a different time.</p>
                        <h4>Is it safe?</h4>
                        <p>Yes. As with all BreachBits activities, Insider Attacks are backed by our <Link to={"/board/CIRT/userguide?section=SafetyPromise"}>Safety Promise</Link>. Our goal is to find a prove weaknesses, not wreak havoc.  We use benign variants of attacks to do this.</p>
                    </DialogContentText>

                </DialogContent>
                <DialogActions>

                    {/*<Button onClick={handleClose} color="primary">*/}
                    {/*    Cancel*/}
                    {/*</Button>*/}
                    <small className={" mx-5"}>Attack ID: {attackId}</small>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default BBDialog;